/**
 * Agent collection & equip screen
 */

import { AGENT_DEFS, RARITY_COLORS, getAgentDef } from '../game/data/agents.js';
import type { AgentDef } from '../game/data/agents.js';
import { getEquipSlotCount, isAgentEquipped } from '../game/systems/agents.js';
import { t } from '../i18n/index.js';
import {
  dispatch, getState, getViewState, onRender,
  setInnerHTML, showToast,
} from './renderer.js';

let tutorialShown = false;

export function initAgentScreen(): void {
  onRender(renderAgentScreen, { alwaysTabs: ['agent'] });
}

function showAgentTutorial(): void {
  if (tutorialShown) return;
  const key = 'agent_tutorial_seen';
  if (localStorage.getItem(key)) return;
  tutorialShown = true;
  localStorage.setItem(key, '1');

  const overlay = document.createElement('div');
  overlay.className = 'agent-tutorial-overlay';
  overlay.innerHTML = `
    <div class="agent-tutorial-card">
      <div class="agent-tutorial-title">${t('agent_tutorial_title')}</div>
      <div class="agent-tutorial-text">${t('agent_tutorial_text')}</div>
      <button class="agent-tutorial-btn" id="agent-tutorial-close">${t('close')}</button>
    </div>
  `;
  document.body.appendChild(overlay);
  overlay.addEventListener('click', (e) => {
    if ((e.target as HTMLElement).classList.contains('agent-tutorial-overlay') ||
        (e.target as HTMLElement).id === 'agent-tutorial-close') {
      overlay.remove();
    }
  });
}

function renderAgentScreen(): void {
  if (getViewState().currentTab !== 'agent') return;
  showAgentTutorial();
  const container = document.getElementById('screen-content');
  if (!container) return;

  const state = getState();
  const ac = state.agentCollection;
  const slotCount = getEquipSlotCount(state);
  const collectedCount = Object.keys(ac.collected).length;

  const slotsHtml = buildSlotsHtml(slotCount);
  const gridHtml = buildGridHtml();

  const html = `
    <div class="agent-screen">
      <div class="agent-header">
        <span class="agent-title">${t('agent_collection_title')}</span>
        <span class="agent-count">${collectedCount}/${AGENT_DEFS.length}</span>
      </div>
      <div class="agent-equipped-section">
        <div class="agent-section-label">${t('agent_equipped')}</div>
        <div class="agent-slots">${slotsHtml}</div>
      </div>
      <div class="agent-grid-section">
        <div class="agent-grid">${gridHtml}</div>
      </div>
    </div>
  `;

  if (setInnerHTML(container, html)) {
    bindAgentEvents(container);
  }
}

function buildSlotsHtml(slotCount: number): string {
  const state = getState();
  const ac = state.agentCollection;
  let html = '';

  for (let i = 0; i < slotCount; i++) {
    const agentId = ac.equipped[i] ?? null;
    if (agentId) {
      const def = getAgentDef(agentId);
      if (def) {
        const color = RARITY_COLORS[def.rarity];
        html += `
          <div class="agent-slot filled" data-slot="${i}" data-agent="${agentId}"
               style="border-color:${color}">
            ${agentImageHtml(def)}
            <div class="agent-slot-name">${t(def.nameKey)}</div>
            <div class="agent-slot-effect" style="color:${color}">
              ${getEffectLabel(def)}
            </div>
          </div>
        `;
        continue;
      }
    }
    html += `
      <div class="agent-slot empty" data-slot="${i}">
        <span class="agent-empty-icon">+</span>
        <span class="agent-empty-label">${t('agent_empty_slot')}</span>
      </div>
    `;
  }

  // Locked slots
  const maxSlots = 5;
  for (let i = slotCount; i < maxSlots; i++) {
    const unlockHint = i === 3 ? 'Prestige 1' : 'Prestige 2';
    html += `
      <div class="agent-slot locked">
        <span class="agent-locked-icon">&#128274;</span>
        <span class="agent-empty-label">${unlockHint}</span>
      </div>
    `;
  }

  return html;
}

function buildGridHtml(): string {
  const state = getState();
  const ac = state.agentCollection;
  let html = '';

  for (const def of AGENT_DEFS) {
    const owned = ac.collected[def.id];
    const color = RARITY_COLORS[def.rarity];
    const equipped = isAgentEquipped(state, def.id);

    if (owned) {
      html += `
        <div class="agent-card owned${equipped ? ' equipped' : ''}"
             data-agent-id="${def.id}" style="border-color:${color}">
          ${agentImageHtml(def)}
          <div class="agent-card-name">${t(def.nameKey)}</div>
          <div class="agent-card-count" style="color:${color}">
            ${owned.count > 1 ? t('agent_count').replace('{n}', String(owned.count)) : ''}
          </div>
          ${equipped ? `<div class="agent-equipped-badge">${t('agent_equipped')}</div>` : ''}
        </div>
      `;
    } else {
      html += `
        <div class="agent-card locked" style="border-color:#333">
          <div class="agent-card-silhouette" style="background:${color}40">?</div>
          <div class="agent-card-name">${t('agent_locked')}</div>
        </div>
      `;
    }
  }

  return html;
}

function agentImageHtml(def: AgentDef): string {
  const color = RARITY_COLORS[def.rarity];
  // Show image with rarity-colored circle fallback if image not found
  return `<div class="agent-card-img" style="background:${color};position:relative">
    <img src="${def.imagePath}" alt="" style="width:100%;height:100%;border-radius:50%;object-fit:cover;position:absolute;inset:0" onerror="this.style.display='none'" />
  </div>`;
}

function getEffectLabel(def: AgentDef): string {
  const key = `agent_effect_${def.effectType}`;
  const pct = effectValueToPercent(def);
  return t(key).replace('{n}', pct);
}

function effectValueToPercent(def: AgentDef): string {
  if (def.effectType === 'boss_spawn_reduce') {
    return `${Math.round(def.effectValue * 100)}%`;
  }
  return `+${Math.round((def.effectValue - 1) * 100)}%`;
}

function bindAgentEvents(container: HTMLElement): void {
  // Tap filled slot to unequip
  container.querySelectorAll('.agent-slot.filled').forEach(el => {
    el.addEventListener('click', () => {
      const slot = parseInt((el as HTMLElement).dataset.slot ?? '-1', 10);
      if (slot < 0) return;
      dispatch({ type: 'UNEQUIP_AGENT', payload: { slotIndex: slot } });
      showToast(t('agent_unequip'));
    });
  });

  // Tap owned card to equip/unequip
  container.querySelectorAll('.agent-card.owned').forEach(el => {
    el.addEventListener('click', () => {
      const agentId = (el as HTMLElement).dataset.agentId;
      if (!agentId) return;

      const state = getState();
      const ac = state.agentCollection;

      // If already equipped, find slot and unequip
      const equippedIdx = ac.equipped.indexOf(agentId);
      if (equippedIdx >= 0) {
        dispatch({ type: 'UNEQUIP_AGENT', payload: { slotIndex: equippedIdx } });
        showToast(t('agent_unequip'));
        return;
      }

      // Find first empty slot
      const slotCount = getEquipSlotCount(state);
      let emptySlot = -1;
      for (let i = 0; i < slotCount; i++) {
        if ((ac.equipped[i] ?? null) === null) {
          emptySlot = i;
          break;
        }
      }
      if (emptySlot < 0) {
        // All slots full
        return;
      }

      dispatch({ type: 'EQUIP_AGENT', payload: { agentId, slotIndex: emptySlot } });
      showToast(t('agent_equip'));
    });
  });
}
